import { useCallback, useEffect, useState } from 'react';
import { Search, Trash2, Eye, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import {
  fetchThreads,
  fetchThreadDetail,
  deleteThread,
  type Thread,
  type ThreadDetail,
} from '@/api/admin';

export default function ThreadsTab() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Sheet for viewing messages
  const [selectedThread, setSelectedThread] = useState<ThreadDetail | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const limit = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchThreads({ page, limit, search });
      setThreads(data.threads);
      setTotal(data.total);
    } catch {
      // error handled by API client
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = () => {
    setPage(1);
    load();
  };

  const handleView = async (threadId: string) => {
    try {
      const detail = await fetchThreadDetail(threadId);
      setSelectedThread(detail);
      setSheetOpen(true);
    } catch {
      // error
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteThread(deleteId);
      setDeleteId(null);
      load();
    } catch {
      // error
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search threads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-9"
          />
        </div>
        <Button onClick={handleSearch} variant="secondary">
          Search
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {threads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No threads found
                  </TableCell>
                </TableRow>
              ) : (
                threads.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium max-w-[300px] truncate">
                      {t.name || 'Untitled'}
                    </TableCell>
                    <TableCell>{t.userIdentifier || '—'}</TableCell>
                    <TableCell>
                      {new Date(t.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleView(t.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(t.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {total} threads total
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center text-sm px-2">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Message viewer sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedThread?.name || 'Thread Messages'}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {selectedThread?.steps?.map((step) =>
              step.type === 'message' ? (
                <div
                  key={step.id}
                  className={`rounded-lg p-3 text-sm ${
                    step.role === 'assistant'
                      ? 'bg-muted'
                      : 'bg-primary/10'
                  }`}
                >
                  <p className="font-semibold text-xs uppercase mb-1 text-muted-foreground">
                    {step.role}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{step.content}</p>
                </div>
              ) : (
                <details
                  key={step.id}
                  className="rounded-lg border border-border text-sm group"
                >
                  <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none hover:bg-muted/50 rounded-lg">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                      step.isError ? 'text-destructive' : 'text-muted-foreground'
                    }`}>
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" />
                      {step.type === 'tool' ? 'Tool' : step.type.toUpperCase()}
                    </span>
                    <span className="font-medium text-xs">{step.name}</span>
                  </summary>
                  <div className="px-3 pb-3 space-y-2">
                    {step.input && (
                      <details className="mt-1">
                        <summary className="text-xs font-medium cursor-pointer text-muted-foreground hover:text-foreground select-none">
                          Input
                        </summary>
                        <pre className="mt-1 p-2 rounded bg-muted/50 text-xs overflow-x-auto whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                          {step.input}
                        </pre>
                      </details>
                    )}
                    {step.output && (
                      <details className="mt-1">
                        <summary className="text-xs font-medium cursor-pointer text-muted-foreground hover:text-foreground select-none">
                          Output
                        </summary>
                        <pre className="mt-1 p-2 rounded bg-muted/50 text-xs overflow-x-auto whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                          {step.output}
                        </pre>
                      </details>
                    )}
                  </div>
                </details>
              )
            )}
            {(!selectedThread?.steps || selectedThread.steps.length === 0) && (
              <p className="text-sm text-muted-foreground">No messages in this thread.</p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete thread?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the thread and all its messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
